import { t } from "@lingui/macro";
import { ApertureIcon, CameraIcon, TrashIcon } from "@phosphor-icons/react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { cn, languageTrans } from "@reactive-resume/utils";
import type { ChangeEvent } from "react";
import { useRef } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { useUploadImage } from "@/client/services/storage";
import { useResumeStore } from "@/client/stores/resume";

import { PictureOptions } from "../picture/options";

const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const PhotoLanguagePanel = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { uploadImage, loading } = useUploadImage();
  const basics = useResumeStore((state) => state.resume.data.basics);
  const setValue = useResumeStore((state) => state.setValue);
  const hasPhoto = Boolean(basics.picture?.url);

  const selectPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_SIZE) {
      toast({
        variant: "destructive",
        title: "Photo non acceptée",
        description: t`Utilisez une image JPG (.jpg), PNG (.png) ou WebP (.webp) de 2 Mo maximum.`,
      });
      return;
    }
    try {
      const response = await uploadImage(file);
      setValue("basics.picture.url", response.data);
    } catch {
      toast({
        variant: "destructive",
        title: "Import impossible",
        description: "La photo n’a pas pu être envoyée.",
      });
    }
  };

  return (
    <div className="grid gap-4 rounded-2xl border border-indigo-100 bg-white p-3.5 shadow-[0_10px_30px_rgba(30,27,75,0.05)] sm:grid-cols-[minmax(0,1fr)_210px] sm:items-center sm:p-4">
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <button
            type="button"
            disabled={loading}
            onClick={() => inputRef.current?.click()}
            className="group relative grid size-[68px] place-items-center overflow-hidden rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50 text-indigo-300 hover:border-indigo-400 sm:size-[74px]"
          >
            {hasPhoto ? (
              <img
                src={basics.picture.url}
                alt="Photo de profil"
                className="size-full object-cover"
              />
            ) : (
              <CameraIcon size={27} weight="duotone" />
            )}
            {loading && (
              <span className="absolute inset-0 grid place-items-center bg-white/80">
                <span className="size-5 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
              </span>
            )}
          </button>
          {hasPhoto && (
            <button
              type="button"
              onClick={() => setValue("basics.picture.url", "")}
              aria-label="Supprimer la photo"
              className="absolute -right-2 -top-2 grid size-7 place-items-center rounded-full border border-rose-100 bg-white text-rose-600 shadow-sm hover:bg-rose-50"
            >
              <TrashIcon size={13} weight="bold" />
            </button>
          )}
        </div>
        <div>
          <p className="text-xs font-bold text-indigo-950">Photo de profil</p>
          <p className="mt-0.5 text-[10px] leading-4 text-slate-500">{t`JPG (.jpg), PNG (.png) ou WebP (.webp) · 2 Mo maximum`}</p>
          <div className="mt-2 flex gap-1.5">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="min-h-8 rounded-lg border border-indigo-200 px-2.5 text-[10px] font-bold text-indigo-700 hover:bg-indigo-50"
            >
              {hasPhoto ? "Remplacer" : "Ajouter"}
            </button>
            {hasPhoto && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="Ajuster la photo"
                    className="grid size-8 place-items-center rounded-lg text-indigo-700 hover:bg-indigo-50"
                  >
                    <ApertureIcon size={16} />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-[min(360px,calc(100vw-2rem))]">
                  <PictureOptions />
                </PopoverContent>
              </Popover>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            aria-label={t`Choisir une photo (.jpg, .jpeg, .png, .webp)`}
            accept=".jpg,.jpeg,.png,.webp"
            className="sr-only"
            onChange={selectPhoto}
          />
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-[11px] font-bold text-indigo-950">Langue du CV</label>
        <Select
          value={basics.cvLanguage || "français"}
          onValueChange={(value) => setValue("basics.cvLanguage", value)}
        >
          <SelectTrigger className="min-h-10 w-full rounded-xl border-slate-200 bg-white text-xs">
            <SelectValue placeholder="Choisir une langue" />
          </SelectTrigger>
          <SelectContent>
            {languageTrans.map((language) => (
              <SelectItem key={language.key} value={language.key}>
                {language.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
